import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AuthGuard } from '@/components/AuthGuard';
import { StaffViewGate } from '@/components/StaffViewGate';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { ConfirmEmailPage } from '@/pages/auth/ConfirmEmailPage';
import { EventsPage } from '@/pages/events/EventsPage';
import { EventDetailPage } from '@/pages/events/EventDetailPage';
import { EventOverviewTab } from '@/pages/events/EventOverviewTab';
import { EventShippingTab } from '@/pages/events/EventShippingTab';
import { EventDocumentsTab } from '@/pages/events/EventDocumentsTab';
import { EventContractsTab } from '@/pages/events/EventContractsTab';
import { EventPaymentTab } from '@/pages/events/EventPaymentTab';
import { EventFaqTab } from '@/pages/events/EventFaqTab';
import { NotFoundPage } from '@/pages/common/NotFoundPage';
import { PayPage } from '@/pages/pay/PayPage';

/** Where payment links land: `/pay/<token>` (the API mints these URLs, see `paymentLinkUrl`). */
const PAY_LINK_PATH = '/pay/:token';
/** Where proposal links land: `/p/<token>` (see `proposalLinkUrl` in the API). */
const PROPOSAL_LINK_PATH = '/p/:token';

/**
 * Application router configuration.
 * Auth routes and the payment-link and proposal-link pages are public; all other routes require
 * authentication via AuthGuard.
 */
export const router = createBrowserRouter([
  // Auth routes (no layout/sidebar)
  { path: '/auth/login', element: <LoginPage /> },
  { path: '/auth/:partner/login', element: <LoginPage /> },
  { path: '/auth/register', element: <RegisterPage /> },
  { path: '/auth/:partner/register', element: <RegisterPage /> },
  { path: '/auth/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/auth/request-password', element: <ForgotPasswordPage /> },
  { path: '/auth/reset-password', element: <ResetPasswordPage /> },
  { path: '/auth/confirm-email', element: <ConfirmEmailPage /> },

  // The no-login pages: the link is the credential, so no AuthGuard.
  { path: PAY_LINK_PATH, element: <PayPage /> },
  // Loaded on demand: it brings pdf.js, which no other My Events page needs.
  {
    path: PROPOSAL_LINK_PATH,
    lazy: async () => ({ Component: (await import('@/pages/proposal/ProposalPage')).ProposalPage }),
  },

  // Authenticated routes (with the shared BCE shell)
  {
    path: '/',
    element: (
      <AuthGuard>
        <StaffViewGate>
          <Outlet />
        </StaffViewGate>
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/events" replace /> },
      { path: 'events', element: <EventsPage /> },
      {
        path: 'events/:id',
        element: <EventDetailPage />,
        children: [
          { path: 'overview', element: <EventOverviewTab /> },
          { path: 'shipping', element: <EventShippingTab /> },
          { path: 'documents', element: <EventDocumentsTab /> },
          { path: 'contracts', element: <EventContractsTab /> },
          { path: 'payment', element: <EventPaymentTab /> },
          { path: 'faq', element: <EventFaqTab /> },
        ],
      },
    ],
  },

  // 404
  { path: '*', element: <NotFoundPage /> },
]);
